/**
 * RENDER — checkpoint gates.
 *
 * Each checkpoint is a glowing ring (torus) the marble must pass through. State is
 * derived entirely from `GameState.checkpointIndex` (the next gate to reach):
 *   - index <  next  → PASSED  : dim green, steady.
 *   - index == next  → PENDING : bright cyan, pulsing (the eye-target).
 *   - index >  next  → FUTURE  : faint cyan, steady.
 * The finish gate is white and the brightest object on screen.
 *
 * A quick scale "pop" plays on the gate the moment it transitions to passed. The
 * idle pulse is tick-driven so it stays in lockstep with the sim (deterministic).
 *
 * Owns its geometries/materials; frees them in `dispose()`.
 */

import * as THREE from 'three';
import type { CourseCheckpoint } from '@/types';
import { PALETTE } from './theme';
import { disposeMaterial } from './three-util';
import { clamp } from './math';

interface Gate {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshStandardMaterial;
  readonly geometry: THREE.TorusGeometry;
  readonly index: number;
  readonly isFinish: boolean;
  readonly baseScale: number;
  /** Eased pop progress in [0,1]; 1 = settled. Drives the pass animation. */
  popT: number;
  /** Whether this gate was passed on the previous frame (to detect the edge). */
  wasPassed: boolean;
}

const POP_SPEED = 6; // pop animation rate (units/sec of progress)

export class Checkpoints {
  readonly group: THREE.Group;
  private readonly gates: Gate[] = [];

  constructor(checkpoints: readonly CourseCheckpoint[]) {
    this.group = new THREE.Group();
    this.group.name = 'checkpoints';
    for (const cp of checkpoints) this.addGate(cp);
  }

  private addGate(cp: CourseCheckpoint): void {
    // Ring radius tracks the trigger radius; thin tube so it reads as a gate.
    const geometry = new THREE.TorusGeometry(cp.radius, Math.max(0.08, cp.radius * 0.06), 12, 40);
    const isFinish = cp.isFinish;
    const material = new THREE.MeshStandardMaterial({
      color: isFinish ? PALETTE.finish : PALETTE.cyan,
      emissive: isFinish ? PALETTE.finish : PALETTE.cyan,
      emissiveIntensity: 0.6,
      roughness: 0.4,
      metalness: 0.0,
      transparent: true,
      opacity: 0.9,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `checkpoint-${cp.index}`;
    mesh.position.set(cp.position[0], cp.position[1], cp.position[2]);
    // Face the ring across the track (marble travels roughly +Z, so the ring
    // opening faces ±Z by default — torus lies in its local XY plane, which is what
    // we want for a gate the marble drives through along Z).
    this.group.add(mesh);
    this.gates.push({
      mesh,
      material,
      geometry,
      index: cp.index,
      isFinish,
      baseScale: 1,
      popT: 1,
      wasPassed: false,
    });
  }

  /**
   * Update gate appearance.
   * @param nextIndex  GameState.checkpointIndex — the next gate to reach.
   * @param tick       fractional sim tick (tick + alpha) for the deterministic pulse.
   * @param dt         render frame delta (seconds) for the pass-pop animation.
   */
  update(nextIndex: number, tick: number, dt: number): void {
    // Smooth, tick-locked pulse for the pending gate (period ≈ 0.9s at 60Hz).
    const pulse = 0.5 + 0.5 * Math.sin(tick * (Math.PI * 2) * (1 / 54));

    for (const g of this.gates) {
      const passed = g.index < nextIndex;
      const pending = g.index === nextIndex;

      // Detect the pass edge to trigger the pop.
      if (passed && !g.wasPassed) {
        g.popT = 0;
      }
      g.wasPassed = passed;

      // Advance the pop animation toward settled.
      if (g.popT < 1) g.popT = clamp(g.popT + dt * POP_SPEED, 0, 1);

      // Color/intensity by state.
      if (passed) {
        if (!g.isFinish) {
          g.material.color.setHex(PALETTE.success);
          g.material.emissive.setHex(PALETTE.success);
        }
        g.material.emissiveIntensity = 0.35;
        g.material.opacity = 0.7;
      } else if (pending) {
        const intensity = g.isFinish ? 0.9 + pulse * 0.6 : 0.6 + pulse * 0.7;
        g.material.emissiveIntensity = intensity;
        g.material.opacity = 0.95;
      } else {
        g.material.emissiveIntensity = g.isFinish ? 0.8 : 0.3;
        g.material.opacity = 0.55;
      }

      // Scale: pending gate breathes slightly; a freshly-passed gate pops then settles.
      const popScale = g.popT < 1 ? 1 + (1 - g.popT) * 0.35 : 1;
      const breathe = pending ? 1 + pulse * 0.04 : 1;
      const s = g.baseScale * popScale * breathe;
      g.mesh.scale.setScalar(s);

      // Slow idle spin on the pending gate to catch the eye without distraction.
      if (pending) g.mesh.rotation.z = tick * 0.01;
    }
  }

  dispose(): void {
    for (const g of this.gates) {
      g.geometry.dispose();
      disposeMaterial(g.material);
    }
    this.gates.length = 0;
    this.group.removeFromParent();
    this.group.clear();
  }
}
